// Metal Stud Configuration

import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';

export const APP_NAME = 'iMetalStud';
export const APP_VERSION = '1.1.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

export type APIPayload = {
  title: string;
  L: string;
  W: string;
  addedWeight: string;
  supports: number;
  sectionName: string;
  numStuds: number;
  restraintLength: string;
  axialLoad: string;
  Pa: string; // pressure history csv
  td: string; // time history csv
  d0: string;
  v0: string;
};

export type ChartKey = 'chart_load' | 'chart_disp' | 'chart_rot' | 'chart_reaction' | 'chart_resistance' | 'chart_PM';

export const LIVE_CHART_ORDER: ChartKey[] = ['chart_disp', 'chart_rot', 'chart_load', 'chart_reaction', 'chart_resistance', 'chart_PM'];

export const PRINT_CHART_ORDER: ChartKey[] = ['chart_load', 'chart_rot', 'chart_disp', 'chart_reaction', 'chart_resistance', 'chart_PM'];

export const PRINT_CHART_ASPECT = {
  chart_load: 'wide',
  chart_disp: 'wide',
  chart_rot: 'wide',
  chart_reaction: 'wide',
  chart_resistance: 'square',
  chart_PM: 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 28 } },
      margin: { l: 86, r: 28, t: 72, b: 108 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  },
  square: {
    width: 600,
    height: 600,
    scale: 2,
    layout: {
      font: { size: 15 },
      title: { font: { size: 28 } },
      margin: { l: 88, r: 28, t: 78, b: 114 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(18rem, 70vw, 25rem)';

export const LIVE_PLOT_SIZE_BY_KEY = {
  chart_load: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_disp: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_rot: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_reaction: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_resistance: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '1 / 1'
  },
  chart_PM: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '1 / 1'
  }
} satisfies Record<ChartKey, LivePlotSizeOptions>;

// prettier-ignore
const StudSections = ['SSMA 400S137-33 (33ksi)', 'SSMA 400S137-43 (33ksi)', 'SSMA 400S137-54 (33ksi)', 'SSMA 400S137-68 (33ksi)','SSMA 400S162-33 (33ksi)', 'SSMA 400S162-43 (33ksi)',
                      'SSMA 400S162-54 (33ksi)', 'SSMA 400S162-68 (33ksi)', 'SSMA 400S200-33 (33ksi)', 'SSMA 400S200-43 (33ksi)', 'SSMA 400S200-54 (33ksi)', 'SSMA 400S200-68 (33ksi)',
                      'SSMA 600S137-33 (33ksi)', 'SSMA 600S137-43 (33ksi)', 'SSMA 600S137-54 (33ksi)', 'SSMA 600S137-68 (33ksi)', 'SSMA 600S137-97 (33ksi)', 'SSMA 600S162-33 (33ksi)',
                      'SSMA 600S162-43 (33ksi)', 'SSMA 600S162-54 (33ksi)', 'SSMA 600S162-68 (33ksi)', 'SSMA 600S162-97 (33ksi)', 'SSMA 600S200-33 (33ksi)', 'SSMA 600S200-43 (33ksi)',
                      'SSMA 600S200-54 (33ksi)', 'SSMA 600S200-68 (33ksi)', 'SSMA 600S200-97 (33ksi)', 'SSMA 600S250-43 (33ksi)', 'SSMA 600S250-54 (33ksi)', 'SSMA 600S250-68 (33ksi)',
                      'SSMA 600S250-97 (33ksi)', 'SSMA 800S137-33 (33ksi)', 'SSMA 800S137-43 (33ksi)', 'SSMA 800S137-54 (33ksi)', 'SSMA 800S137-68 (33ksi)', 'SSMA 800S137-97 (33ksi)',
                      'SSMA 800S162-33 (33ksi)', 'SSMA 800S162-43 (33ksi)', 'SSMA 800S162-54 (33ksi)', 'SSMA 800S162-68 (33ksi)', 'SSMA 800S162-97 (33ksi)', 'SSMA 800S200-33 (33ksi)',
                      'SSMA 800S200-43 (33ksi)', 'SSMA 800S200-54 (33ksi)', 'SSMA 800S200-68 (33ksi)', 'SSMA 800S200-97 (33ksi)', 'SSMA 800S250-43 (33ksi)', 'SSMA 800S250-54 (33ksi)',
                      'SSMA 800S250-68 (33ksi)', 'SSMA 800S250-97 (33ksi)', 'SSMA 1000S162-43 (33ksi)', 'SSMA 1000S162-54 (33ksi)', 'SSMA 1000S162-68 (33ksi)', 'SSMA 1000S162-97 (33ksi)',
                      'SSMA 1000S200-43 (33ksi)', 'SSMA 1000S200-54 (33ksi)', 'SSMA 1000S200-68 (33ksi)', 'SSMA 1000S200-97 (33ksi)', 'SSMA 1000S250-43 (33ksi)', 'SSMA 1000S250-54 (33ksi)',
                      'SSMA 1000S250-68 (33ksi)', 'SSMA 1000S250-97 (33ksi)', 'SSMA 1200S162-54 (33ksi)', 'SSMA 1200S162-68 (33ksi)', 'SSMA 1200S162-97 (33ksi)', 'SSMA 1200S200-54 (33ksi)',
                      'SSMA 1200S200-68 (33ksi)', 'SSMA 1200S200-97 (33ksi)', 'SSMA 1200S250-54 (33ksi)', 'SSMA 1200S250-68 (33ksi)', 'SSMA 1200S250-97 (33ksi)', 'SSMA 400S137-54 (50ksi)',
                      'SSMA 400S137-68 (50ksi)', 'SSMA 400S162-54 (50ksi)', 'SSMA 400S162-68 (50ksi)', 'SSMA 400S200-54 (50ksi)', 'SSMA 400S200-68 (50ksi)','SSMA 600S137-54 (50ksi)',
                      'SSMA 600S137-68 (50ksi)', 'SSMA 600S137-97 (50ksi)', 'SSMA 600S162-54 (50ksi)', 'SSMA 600S162-68 (50ksi)', 'SSMA 600S162-97 (50ksi)', 'SSMA 600S200-54 (50ksi)',
                      'SSMA 600S200-68 (50ksi)', 'SSMA 600S200-97 (50ksi)', 'SSMA 600S250-54 (50ksi)', 'SSMA 600S250-68 (50ksi)', 'SSMA 600S250-97 (50ksi)', 'SSMA 800S137-54 (50ksi)',
                      'SSMA 800S137-68 (50ksi)', 'SSMA 800S137-97 (50ksi)', 'SSMA 800S162-54 (50ksi)', 'SSMA 800S162-68 (50ksi)', 'SSMA 800S162-97 (50ksi)', 'SSMA 800S200-54 (50ksi)',
                      'SSMA 800S200-68 (50ksi)', 'SSMA 800S200-97 (50ksi)', 'SSMA 800S250-54 (50ksi)', 'SSMA 800S250-68 (50ksi)', 'SSMA 800S250-97 (50ksi)', 'SSMA 1000S162-54 (50ksi)',
                      'SSMA 1000S162-68 (50ksi)','SSMA 1000S162-97 (50ksi)', 'SSMA 1000S200-54 (50ksi)', 'SSMA 1000S200-68 (50ksi)', 'SSMA 1000S200-97 (50ksi)', 'SSMA 1000S250-54 (50ksi)',
                      'SSMA 1000S250-68 (50ksi)', 'SSMA 1000S250-97 (50ksi)', 'SSMA 1200S162-54 (50ksi)', 'SSMA 1200S162-68 (50ksi)', 'SSMA 1200S162-97 (50ksi)', 'SSMA 1200S200-54 (50ksi)',
                      'SSMA 1200S200-68 (50ksi)', 'SSMA 1200S200-97 (50ksi)', 'SSMA 1200S250-54 (50ksi)', 'SSMA 1200S250-68 (50ksi)', 'SSMA 1200S250-97 (50ksi)',
]

export const SECTION_NAMES: DropdownOption[] = StudSections.map(s => ({ value: s, label: s }));

export type APIResponse = {
  summary: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
  cross_section?: ChartImage;
};

// Plotly chart images are generated client-side for print/save.
export type MetalStudResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};
